import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LayoutDashboard, Users, ClipboardList, ChefHat, FileText, Loader2, Calendar, ArrowRight, Settings, GripVertical, Check, UtensilsCrossed, BarChart3, Coffee, Sun, Moon } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

// All available quick actions
const ALL_QUICK_ACTIONS = [
  { 
    id: 'orders',
    title: 'Presa Comande', 
    description: 'Registra gli ordini dei pazienti',
    icon: ClipboardList, 
    path: '/orders',
    color: 'bg-blue-700'
  },
  { 
    id: 'kitchen',
    title: 'Riepilogo Cucina', 
    description: 'Totali per preparazione',
    icon: ChefHat, 
    path: '/kitchen',
    color: 'bg-amber-600'
  },
  { 
    id: 'sheets',
    title: 'Cartellini', 
    description: 'Stampa per i vassoi',
    icon: FileText, 
    path: '/sheets',
    color: 'bg-emerald-600'
  },
  { 
    id: 'patients',
    title: 'Pazienti', 
    description: 'Gestisci anagrafica',
    icon: Users, 
    path: '/patients',
    color: 'bg-purple-600'
  },
  { 
    id: 'menu',
    title: 'Menu', 
    description: 'Configura il menu settimanale',
    icon: UtensilsCrossed, 
    path: '/menu',
    color: 'bg-rose-600'
  },
  { 
    id: 'history',
    title: 'Storico', 
    description: 'Statistiche ordini passati',
    icon: BarChart3, 
    path: '/history',
    color: 'bg-slate-600'
  }
];

const DEFAULT_SHORTCUTS = ['orders', 'kitchen', 'sheets', 'patients'];

export const DashboardPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayMenu, setTodayMenu] = useState({ colazione: [], pranzo: [], cena: [] });
  const [loading, setLoading] = useState(true);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);
  
  // Load shortcuts from localStorage
  const [selectedShortcuts, setSelectedShortcuts] = useState(() => {
    const saved = localStorage.getItem('dashboard_shortcuts');
    return saved ? JSON.parse(saved) : DEFAULT_SHORTCUTS;
  });

  const today = format(new Date(), 'yyyy-MM-dd');
  const dayNames = ['domenica', 'lunedi', 'martedi', 'mercoledi', 'giovedi', 'venerdi', 'sabato'];
  const todayDayCode = dayNames[new Date().getDay()];
  
  // ISO week number calculation (same as MenuConfigPage)
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7; // Sunday = 7
    d.setUTCDate(d.getUTCDate() + 4 - dayNum); // Set to Thursday of current week
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };
  const weekNumber = getWeekNumber(new Date());

  useEffect(() => {
    const fetchStats = async () => {
      const currentYear = new Date().getFullYear();
      try {
        const [patientsRes, ordersRes, categoriesRes, colazioneMenuRes, pranzoMenuRes, cenaMenuRes] = await Promise.all([
          api.get('/patients'),
          api.get('/orders', { params: { date: today } }),
          api.get('/categories'),
          api.get('/menu', { params: { week_number: weekNumber, year: currentYear, day: todayDayCode, meal_type: 'colazione' } }),
          api.get('/menu', { params: { week_number: weekNumber, year: currentYear, day: todayDayCode, meal_type: 'pranzo' } }),
          api.get('/menu', { params: { week_number: weekNumber, year: currentYear, day: todayDayCode, meal_type: 'cena' } })
        ]);

        const patients = patientsRes.data;
        const orders = ordersRes.data;
        const patientsWithOrders = new Set(orders.map(o => o.patient_id)).size;

        setTodayMenu({
          colazione: colazioneMenuRes.data || [],
          pranzo: pranzoMenuRes.data || [],
          cena: cenaMenuRes.data || []
        });

        setStats({
          totalPatients: patients.length,
          presentPatients: patients.filter(p => p.is_present).length,
          patientsWithOrders,
          totalOrders: orders.length,
          categories: categoriesRes.data.length,
          inReparto: patients.filter(p => p.is_present && p.meal_location === 'reparto').length,
          inRistorante: patients.filter(p => p.is_present && p.meal_location === 'ristorante').length
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [api, today, weekNumber, todayDayCode]);

  // Save shortcuts to localStorage when changed
  const saveShortcuts = (shortcuts) => {
    setSelectedShortcuts(shortcuts);
    localStorage.setItem('dashboard_shortcuts', JSON.stringify(shortcuts));
  };

  const toggleShortcut = (id) => {
    if (selectedShortcuts.includes(id)) {
      saveShortcuts(selectedShortcuts.filter(s => s !== id));
    } else {
      saveShortcuts([...selectedShortcuts, id]);
    }
  };

  const resetShortcuts = () => {
    saveShortcuts(DEFAULT_SHORTCUTS);
  };

  // Get active quick actions based on selection
  const quickActions = selectedShortcuts
    .map(id => ALL_QUICK_ACTIONS.find(a => a.id === id))
    .filter(Boolean);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          Buongiorno, {user?.username}
        </h1>
        <p className="text-slate-500 mt-1 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          {format(new Date(), "EEEE d MMMM yyyy", { locale: it })}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200 hover-lift" data-testid="stat-patients">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{stats?.totalPatients || 0}</p>
                <p className="text-sm text-slate-500">Pazienti attivi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover-lift" data-testid="stat-orders-patients">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <ClipboardList className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{stats?.patientsWithOrders || 0}</p>
                <p className="text-sm text-slate-500">Con ordini oggi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover-lift" data-testid="stat-total-orders">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{stats?.totalOrders || 0}</p>
                <p className="text-sm text-slate-500">Piatti ordinati</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover-lift" data-testid="stat-categories">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{stats?.categories || 0}</p>
                <p className="text-sm text-slate-500">Categorie menu</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Menu del Giorno */}
      <Card className="border-slate-200" data-testid="today-menu">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <UtensilsCrossed className="w-5 h-5 text-blue-600" />
            Menu del Giorno - {format(new Date(), 'EEEE d MMMM', { locale: it })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Colazione */}
            <div className="bg-amber-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Coffee className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-amber-800">Colazione</h3>
              </div>
              {todayMenu.colazione.length > 0 ? (
                <ul className="space-y-1">
                  {todayMenu.colazione.map((item, idx) => (
                    <li key={idx} className="text-sm text-amber-700 flex items-start gap-2">
                      <span className="text-amber-400">•</span>
                      <span><strong className="capitalize">{item.category_code}:</strong> {item.dish_name}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-amber-600 italic">Menu fisso colazione</p>
              )}
            </div>

            {/* Pranzo */}
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sun className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-blue-800">Pranzo</h3>
              </div>
              {todayMenu.pranzo.length > 0 ? (
                <ul className="space-y-1">
                  {todayMenu.pranzo.map((item, idx) => (
                    <li key={idx} className="text-sm text-blue-700 flex items-start gap-2">
                      <span className="text-blue-400">•</span>
                      <span><strong className="capitalize">{item.category_code}:</strong> {item.dish_name}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-blue-600 italic">Nessun piatto configurato</p>
              )}
            </div>

            {/* Cena */}
            <div className="bg-indigo-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Moon className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold text-indigo-800">Cena</h3>
              </div>
              {todayMenu.cena.length > 0 ? (
                <ul className="space-y-1">
                  {todayMenu.cena.map((item, idx) => (
                    <li key={idx} className="text-sm text-indigo-700 flex items-start gap-2">
                      <span className="text-indigo-400">•</span>
                      <span><strong className="capitalize">{item.category_code}:</strong> {item.dish_name}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-indigo-600 italic">Copia da pranzo</p>
              )}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-4 text-sm text-slate-600">
            <span>📍 In Reparto: <strong>{stats?.inReparto || 0}</strong></span>
            <span>🍽️ Al Ristorante: <strong>{stats?.inRistorante || 0}</strong></span>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-slate-900">Azioni Rapide</h2>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShortcutsDialogOpen(true)}
            data-testid="customize-shortcuts-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Personalizza
          </Button>
        </div>
        {quickActions.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
            {quickActions.map((action, idx) => (
              <Card 
                key={action.id}
                className="border-slate-200 cursor-pointer hover-lift group animate-fade-in"
                onClick={() => navigate(action.path)}
                data-testid={`quick-action-${action.path.slice(1)}`}
              >
                <CardContent className="pt-6">
                  <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <action.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">{action.description}</p>
                  <div className="flex items-center gap-1 text-blue-700 text-sm mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    Apri <ArrowRight className="w-4 h-4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="py-8 text-center text-slate-500">
              <p>Nessuna scorciatoia selezionata</p>
              <Button 
                variant="link" 
                onClick={() => setShortcutsDialogOpen(true)}
                className="mt-2"
              >
                Aggiungi scorciatoie
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Shortcuts Customization Dialog */}
      <Dialog open={shortcutsDialogOpen} onOpenChange={setShortcutsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Personalizza Scorciatoie
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Seleziona le azioni rapide da mostrare nella dashboard.
            </p>
            
            <div className="space-y-2">
              {ALL_QUICK_ACTIONS.map(action => (
                <div 
                  key={action.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedShortcuts.includes(action.id) 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                  onClick={() => toggleShortcut(action.id)}
                  data-testid={`shortcut-toggle-${action.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center flex-shrink-0`}>
                    <action.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900">{action.title}</p>
                    <p className="text-xs text-slate-500">{action.description}</p>
                  </div>
                  {selectedShortcuts.includes(action.id) && (
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
            
            <div className="flex gap-2 pt-4 border-t">
              <Button variant="outline" onClick={resetShortcuts} className="flex-1">
                Reset Default
              </Button>
              <Button onClick={() => setShortcutsDialogOpen(false)} className="flex-1 bg-blue-700 hover:bg-blue-800">
                Chiudi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Today Status */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-blue-700" />
            Stato Odierno
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Badge 
              className={stats?.patientsWithOrders === stats?.totalPatients ? 'bg-emerald-600' : 'bg-amber-600'}
            >
              {stats?.patientsWithOrders || 0} / {stats?.totalPatients || 0} pazienti
            </Badge>
            <span className="text-slate-500">
              {stats?.patientsWithOrders === stats?.totalPatients 
                ? 'Tutti i pazienti hanno ordini' 
                : `${(stats?.totalPatients || 0) - (stats?.patientsWithOrders || 0)} pazienti senza ordini`
              }
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
